import { useCallback, useEffect, useRef, useState } from 'react';
import { createService, listServices } from '../api/services.api';
import type { CreateServiceInput, Service } from '../types/service.types';

export function useServices(tenantId?: number) {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createSuccess, setCreateSuccess] = useState(false);
  const mountedRef = useRef(true);
  const requestGenerationRef = useRef(0);

  const loadServices = useCallback(() => {
    const generation = ++requestGenerationRef.current;
    const isCurrentRequest = () =>
      mountedRef.current && requestGenerationRef.current === generation;

    setLoading(true);
    setError(null);

    return listServices(tenantId)
      .then((data) => {
        if (isCurrentRequest()) setServices(data);
      })
      .catch(() => {
        if (isCurrentRequest()) setError('خطا در دریافت فهرست سرویس‌ها');
      })
      .finally(() => {
        if (isCurrentRequest()) setLoading(false);
      });
  }, [tenantId]);

  const create = useCallback(async (input: CreateServiceInput) => {
    setCreating(true);
    setCreateError(null);
    setCreateSuccess(false);
    try {
      const service = await createService(input);
      await loadServices();
      if (mountedRef.current) setCreateSuccess(true);
      return service;
    } catch {
      if (mountedRef.current) setCreateError('خطا در ایجاد سرویس');
      return null;
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  }, [loadServices]);

  useEffect(() => {
    mountedRef.current = true;
    void loadServices();
    return () => {
      mountedRef.current = false;
    };
  }, [loadServices]);

  return {
    services,
    loading,
    error,
    creating,
    createError,
    createSuccess,
    create,
    refetch: loadServices,
  };
}
